import Button from "../atoms/button";
import Text from "../atoms/text";
import View from "../atoms/view";
import cn from "@/utils/cn";
import ButtonIcon from "./buttons/icon";

export type PaginationProps = {
  totalPages: number;
  currentPage: number;
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  startIndex: number;
  endIndex: number;
  total: number;
};
/**
 * @name Pagination
 * @description Renders a pagination component with prev/next buttons and page numbers.
 *
 * @param {PaginationProps} props - Component props.
 * @param {number} props.totalPages - The total number of pages.
 * @param {number} props.currentPage - The current active page number.
 * @param {(page: number) => void} props.goToPage - Function to navigate to a specific page.
 * @param {number} props.total - The total number of entries.
 * @param {number} props.startIndex - The index of the first entry on the current page.
 * @param {number} props.endIndex - The index of the last entry on the current page.
 * @param {() => void} props.nextPage - Function to navigate to the next page.
 * @param {() => void} props.prevPage - Function to navigate to the previous page.
 * @returns {JSX.Element} The rendered pagination component.
 */
const Pagination = ({
  totalPages,
  currentPage,
  goToPage,
  total,
  startIndex,
  endIndex,
  nextPage,
  prevPage
}: PaginationProps) => {
  const pages = Array.from({ length: totalPages }, (_, index) => index + 1);

  return (
    <View className="flex col-span-12 gap-4 sm:justify-between justify-center items-center flex-wrap">
      <Text>
        Showing {startIndex + 1} to {endIndex + 1} of {total} entries
      </Text>

      <View
        type="ul"
        className="flex gap-2 md:gap-3 flex-wrap md:font-semibold items-center"
      >
        <View type="li">
          <ButtonIcon
            action={prevPage}
            disabled={currentPage === 1}
            iconLeft={{ name: "fa-solid fa-chevron-left" }}
            className={cn(
              "hover:bg-primary disabled:hover:bg-transparent disabled:hover:text-primary text-primary rtl:rotate-180 disabled:cursor-not-allowed hover:text-n0 border md:w-10 duration-300 md:h-10 w-8 h-8 flex items-center rounded-full justify-center border-primary"
            )}
          />
        </View>
        {pages.map((page) => (
          <View type="li" key={page}>
            <Button
              action={() => goToPage(page)}
              className={cn(
                "hover:bg-primary text-primary hover:text-n0 border md:w-10 duration-300 md:h-10 w-8 h-8 flex items-center rounded-full justify-center border-primary",
                {
                  "bg-primary text-n0": currentPage === page
                }
              )}
            >
              {page}
            </Button>
          </View>
        ))}
        <View type="li">
          <ButtonIcon
            action={nextPage}
            disabled={currentPage === totalPages}
            iconLeft={{ name: "fa-solid fa-chevron-right" }}
            className={cn(
              "hover:bg-primary disabled:hover:bg-transparent disabled:hover:text-primary rtl:rotate-180 text-primary disabled:cursor-not-allowed hover:text-n0 border md:w-10 duration-300 md:h-10 w-8 h-8 flex items-center rounded-full justify-center border-primary"
            )}
          />
        </View>
      </View>
    </View>
  );
};

export default Pagination;
